// components/table.tsx
import React, {useRef, useState} from 'react';
import { Box, Button, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay } from '@chakra-ui/react';
import { useDrag } from 'react-dnd';
import { TableShape } from './types';
import {CloseIcon} from "@chakra-ui/icons";
import {useDeleteTable} from "../../../../api/tables/useDeleteTable";

interface TableProps {
    id: string;
    positionX: number;
    positionY: number;
    shape: TableShape;
    tableNumber: string;
    moveTable: (id: string, positionX: number, positionY: number) => void;
    removeTable: (id: string) => void;
}

const getShapeStyle = (shape: TableShape): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
        position: 'absolute',
        border: '1px solid white',
        borderRadius: '10px',
        backgroundColor: '#01011E',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        textAlign: 'center',
        fontSize: '12px'
    };

    switch (shape) {
        case TableShape.SQUARE:
            return { ...baseStyle, width: '60px', height: '60px' };
        case TableShape.CIRCLE:
            return { ...baseStyle, width: '60px', height: '60px', borderRadius: '50%' };
        case TableShape.DIAMOND:
            return {
                ...baseStyle,
                width: '60px',
                height: '60px',
                transform: 'rotate(45deg)',
            };
        case TableShape.RECTANGLE:
            return { ...baseStyle, width: '90px', height: '60px' };
        default:
            return baseStyle;
    }
};

const Table: React.FC<TableProps> = ({ id, positionX, positionY, shape, tableNumber, removeTable }) => {
    const [{ isDragging }, drag] = useDrag({
        type: 'TABLE',
        item: { id, positionX, positionY, shape, tableNumber },
        collect: monitor => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const style: React.CSSProperties = {
        ...getShapeStyle(shape),
        left: positionX,
        top: positionY,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
    };

    const textStyle: React.CSSProperties = shape === TableShape.DIAMOND
        ? { transform: 'rotate(-45deg)' }
        : {};

    const { deleteTable } = useDeleteTable();

    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const cancelRef = useRef<HTMLButtonElement>(null);

    const openConfirm = () => setIsConfirmOpen(true);
    const closeConfirm = () => setIsConfirmOpen(false);

    const handleDelete = async () => {
        try {
            // Call your deletion hook
            await deleteTable(id);
            // Remove the table from UI state via the provided callback
            removeTable(id);
        } catch (error) {
            console.error("Table deletion failed", error);
        } finally {
            closeConfirm();
        }
    };

    return (
        <Box
            ref={drag}
            style={style}
        >
            <Button
                onClick={openConfirm}
                colorScheme="yellow"
                size="xs"
                position="absolute"
                top="-2px"
                right="-4px"
                borderRadius="full"
                padding={0}
                minWidth="auto"
                width="20px"
                height="20px"
            >
                <CloseIcon w={2} h={2} />
            </Button>
            <Box style={textStyle}>
                Table {tableNumber}
            </Box>

            <AlertDialog
                isOpen={isConfirmOpen}
                leastDestructiveRef={cancelRef}
                onClose={closeConfirm}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Table
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            Are you sure you want to delete this table? This action cannot be undone.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={closeConfirm}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={handleDelete} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

        </Box>
    );
};

export default Table;
